import axios from "axios";
import { addPending, shouldQueue, buildLocalPedido, syncAll as syncOfflineQueue } from "./offlineQueue";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("atlas_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/**
 * Interceptor de offline para POST /pedidos.
 * Si no hay red al momento de armar un pedido, en vez de fallar, lo
 * guarda en IndexedDB y devuelve una respuesta sintética con id local.
 * Además normaliza `error.response.data.detail` a string cuando viene como
 * lista/objeto (Pydantic 422 errors), para evitar romper React.
 */
api.interceptors.response.use(
  (r) => {
    // Después de cualquier mutación exitosa (POST/PATCH/DELETE), notificamos a la
    // campanita para refresque sin esperar el polling. Excluimos GET, login y notif
    // endpoints para no crear loops.
    try {
      const method = (r.config?.method || "").toLowerCase();
      const url = String(r.config?.url || "");
      if (typeof window !== "undefined"
          && ["post", "patch", "delete", "put"].includes(method)
          && !url.startsWith("/auth")
          && !url.startsWith("/notificaciones")) {
        window.dispatchEvent(new CustomEvent("notif-refresh"));
      }
    } catch { /* fail-silent */ }
    return r;
  },
  async (error) => {
    // Normalize detail to always be a string for downstream toast rendering
    const detail = error?.response?.data?.detail;
    if (detail && typeof detail !== "string") {
      try {
        if (Array.isArray(detail)) {
          error.response.data.detail = detail
            .map((d) => (d?.msg ? `${d.msg}${d.loc ? ` (${(d.loc || []).slice(1).join(".")})` : ""}` : JSON.stringify(d)))
            .join(" · ");
        } else {
          error.response.data.detail = JSON.stringify(detail);
        }
      } catch { error.response.data.detail = "Error de validación"; }
    }
    const cfg = error?.config;
    // Network error only (no response). Sólo interceptamos POST /pedidos.
    const isNetworkError = !error?.response;
    if (isNetworkError && cfg && cfg.method === "post" && /\/pedidos\/?$/.test(cfg.url || "") && shouldQueue()) {
      try {
        const payload = typeof cfg.data === "string" ? JSON.parse(cfg.data) : cfg.data;
        // Snapshot básico para mostrar en la UI mientras espera sync
        const snapshot = {
          cliente_nombre: payload?.__snapshot?.cliente_nombre || "Cliente sin conexión",
          cliente_id: payload?.cliente_id,
          items: payload?.items || [],
          total: payload?.__snapshot?.total || 0,
          notas: payload?.notas,
        };
        // Limpiar campos de conveniencia
        const cleanPayload = { ...payload };
        delete cleanPayload.__snapshot;
        const rec = await addPending({ payload: cleanPayload, snapshot });
        const fake = buildLocalPedido({ payload: cleanPayload, snapshot, localId: rec.localId });
        return Promise.resolve({ data: fake, status: 202, headers: {}, config: cfg, __offline: true });
      } catch (e) {
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

// Auto-sync on reconnect
if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    syncOfflineQueue(api).catch(() => {});
  });
  // Also try to sync when app opens
  window.addEventListener("load", () => {
    if (navigator.onLine) syncOfflineQueue(api).catch(() => {});
  });
}

export const syncOffline = () => syncOfflineQueue(api);

export const formatCOP = (n) => {
  if (n === null || n === undefined || isNaN(n)) return "$0";
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 0,
  }).format(n);
};

export const formatNumber = (n) => new Intl.NumberFormat("es-CO").format(n || 0);

// Fecha local (Colombia) en YYYY-MM-DD. NO usar toISOString().slice(0,10): eso da la fecha UTC,
// que desde las 7 p.m. de Bogotá ya es el día siguiente (los pedidos "saltaban" de mes).
export const toISODateLocal = (d) => {
  const x = d instanceof Date ? d : new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
};
export const hoyISO = () => toISODateLocal(new Date());
export const mesISOLocal = (d = new Date()) => toISODateLocal(d).slice(0, 7);

export const formatDate = (iso) => {
  if (!iso) return "";
  try {
    // Si es solo fecha (YYYY-MM-DD), formatear directo sin pasar por Date
    // para EVITAR el bug de zona horaria (UTC midnight → día anterior en Colombia UTC-5).
    if (typeof iso === "string" && /^\d{4}-\d{2}-\d{2}$/.test(iso.slice(0, 10)) && iso.length <= 10) {
      const [y, m, d] = iso.slice(0, 10).split("-");
      return `${d}/${m}/${y}`;
    }
    // Formato Colombia: DD/MM/YYYY (numérico, sin nombres de mes)
    return new Intl.DateTimeFormat("es-CO", { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(iso));
  } catch { return iso; }
};

export const formatShortDate = (iso) => {
  if (!iso) return "";
  try {
    // Igual: para fecha pura, sin Date object.
    if (typeof iso === "string" && /^\d{4}-\d{2}-\d{2}$/.test(iso.slice(0, 10)) && iso.length <= 10) {
      const [, m, d] = iso.slice(0, 10).split("-");
      return `${d}/${m}`;
    }
    // DD/MM (dos dígitos cada uno) — ideal para tablas compactas
    return new Intl.DateTimeFormat("es-CO", { day: "2-digit", month: "2-digit" }).format(new Date(iso));
  } catch { return iso; }
};

export const formatDateTime = (iso) => {
  if (!iso) return "";
  try {
    // DD/MM/YYYY HH:mm — para timestamps completos
    return new Intl.DateTimeFormat("es-CO", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: false,
    }).format(new Date(iso));
  } catch { return iso; }
};
